import { map, switchMap, catchError, concatMap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoggerService } from '@content-platform/web-angular-shared-util-logging';
import { Observable, of, zip } from 'rxjs';
import { Application, Permission, UserPermissionList } from '../models';
import { ApiParams } from './api-params';
import { Resource } from './resource';
import {
  MOCK_APPLICATION_ACCESS_LIST,
  MOCK_PERMISSION_LIST,
  USE_MOCK_DATA_ON_ERROR,
} from './mock-data';
import { WebAngularSharedDataAccessFeatureFlagApisService } from '@content-platform/web-angular-shared-data-access-feature-flag-api';

@Injectable()
export class UserPermissionListResource extends Resource<UserPermissionList> {
  constructor(http: HttpClient, logger: LoggerService, private apiParams: ApiParams,
    protected featureFlagSvc: WebAngularSharedDataAccessFeatureFlagApisService) {
    super(http, logger.instance('UserPermissionListResource'),
      featureFlagSvc);
  }

  getUrl() {
    return '';
  }

  get(uuid: string): Observable<UserPermissionList> {
    return zip(this.getUserApps(uuid), this.getUserPermissions(uuid)).pipe(
      switchMap((data) => {
        const permissions = data[1];
        const applications = data[0];
        return of({
          uuid,
          applications,
          permissions,
        }).pipe(switchMap((userPermissionList) => of(userPermissionList)));
      })
    );
  }

  get entityName(): string {
    return 'User Permission List';
  }

  private getUserApps(uuid: string): Observable<Application[]> {
    return this.featureFlagSvc.getFlagAsync('use-new-gateway').pipe(
      catchError(() => {
        // does not exist
        return of(false);
      }),
      concatMap((flag) => {
        return this.http
          .get<Application[]>(this.apiParams.getUrl('userApplications', { uuid: uuid }, flag), {
          headers: this.getHeaders(),
        })
          .pipe(
            catchError((error) =>
              USE_MOCK_DATA_ON_ERROR
                ? of(MOCK_APPLICATION_ACCESS_LIST)
                : this.handleError(`Retrieving User Apps Failed`, error)
            )
          );
      })
    );
  }

  private getUserPermissions(uuid: string): Observable<Permission[]> {
    return this.featureFlagSvc.getFlagAsync('use-new-gateway').pipe(
      catchError(() => {
        // does not exist
        return of(false);
      }),
      concatMap((flag) => {
        return this.http
          .get<Permission[] | UserPermissionList[]>(
          this.apiParams.getUrl('userPermissions', { uuid: uuid }, flag),
          {
            headers: this.getHeaders(),
          }
        )
          .pipe(
            map((permissionsList: Permission[] | UserPermissionList[]) => {
              if (
                (<UserPermissionList[]>permissionsList).length > 0 &&
                (<UserPermissionList[]>permissionsList)[0].permissions
              ) {
                const permissions = [].concat(
                  ...(<UserPermissionList[]>permissionsList).map((list) => list.permissions)
                );
                return permissions;
              }
              return permissionsList as Permission[];
            }),
            catchError((error) =>
              USE_MOCK_DATA_ON_ERROR
                ? of(MOCK_PERMISSION_LIST)
                : this.handleError(`Retrieving User Permissions Failed`, error)
            )
          );
      })
    );
    
  }
}
